import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import type { SanityCaseStudyCard } from "@ouster/shared";
import { AnimatePresence } from "framer-motion";
import dynamic from "next/dynamic";
import React from "react";
import { hasItems } from "~/features/app/utils";
import { InfoWindow } from "~/features/ui/map/info-window";
const Map = dynamic(() => import("~/features/ui/map/map").then(mod => mod.Map), {
  ssr: false
});
type Location = {
  lat: number;
  lng: number;
};
function getActiveCaseStudy(caseStudies: SanityCaseStudyCard[], coordinate?: google.maps.LatLng) {
  return caseStudies.find(caseStudy => caseStudy.locations?.some(location => location.lat === coordinate?.lat() && location.lng === coordinate?.lng()));
}
type Props = {
  caseStudies: SanityCaseStudyCard[];
};
export function CaseStudiesMap({
  caseStudies
}: Props) {
  const [activeCoordinate, setActiveCoordinate] = React.useState<google.maps.LatLng>();
  const activeItem = getActiveCaseStudy(caseStudies, activeCoordinate);
  const locations = React.useMemo<Location[]>(() => caseStudies.flatMap(caseStudy => (caseStudy.locations ?? []).flatMap(location => typeof location.lat === "number" && typeof location.lng === "number" ? [{
    lat: location.lat,
    lng: location.lng
  }] : [])), [caseStudies]);
  if (!hasItems(locations)) {
    return null;
  }
  return (
    // Unlike the card grids, the map is a solid band: an opaque full-width white
    // surface that covers the route's dotted Pattern, closed by a hairline rule
    // top and bottom. The bottom rule is what separates it from the cards below,
    // and it only exists when there is a map to separate.
    <_StyledDiv $_css={[{
      "position": "relative",
      "zIndex": "10",
      "borderTopWidth": "1px",
      "borderBottomWidth": "1px",
      "borderColor": "var(--stroke-primary)",
      "--tw-bg-opacity": "1",
      "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
    }]}>
      <_StyledDiv2>
        <_StyledDiv3>
          <Map locations={locations} activeLocation={activeCoordinate} onMarkerClick={setActiveCoordinate} />
          <AnimatePresence>
            {activeItem && <InfoWindow caseStudy={activeItem} onClose={() => setActiveCoordinate(undefined)} />}
          </AnimatePresence>
        </_StyledDiv3>
      </_StyledDiv2>
    </_StyledDiv>
  );
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-rspk7m-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-rspk7m-1"
})({
  "overflow": "hidden",
  "borderRadius": "2px",
  "padding": "1.6rem",
  "@media (min-width: 1024px)": {
    "padding": "4rem"
  }
});
var _StyledDiv3 = /*#__PURE__*/_styled3("div").withConfig({
  displayName: "case-studies-map___StyledDiv3",
  componentId: "sc-1gv8i62-0"
})({
  "position": "relative",
  "height": "calc(100vh - 80px)",
  "maxHeight": "900px"
});